import { useMemo } from "react";
import { AssessmentTab, AssessmentLayer } from "../types";
import { CheckboxHierarchyState } from "./useCheckboxHierarchy";
import { getActiveLayers } from "../utils";

export interface LayerActivationState {
  activeLayers: AssessmentLayer[];
}

/**
 * Hook that determines which layers are currently active based on checkbox states.
 *
 * This hook bridges the gap between checkbox UI state and the actual layers that
 * should be displayed or processed. It handles both simple layer-based structures
 * and complex section-based structures.
 *
 * Use cases:
 * - Determining which map layers to display based on user selections
 * - Filtering data to show only for selected layers
 * - Generating reports that include only active layers
 * - Triggering side effects when layer activation changes
 *
 * @param assessmentTabs - The hierarchical structure of tabs, groups, and layers
 * @param checkboxState - Current state of all checkboxes in the hierarchy
 * @param supportsSectionIds - Whether to use section-based logic or simple layer logic
 * @returns Object containing the list of active layers
 */
export function useLayerActivation(
  assessmentTabs: AssessmentTab[],
  checkboxState: CheckboxHierarchyState,
  supportsSectionIds: boolean
): LayerActivationState {
  const activeLayers = useMemo(() => {
    return getActiveLayers(assessmentTabs, checkboxState, supportsSectionIds);
  }, [assessmentTabs, checkboxState, supportsSectionIds]);

  return { activeLayers };
}
